// packages/web-ui/src/AppShell/layout-modes.ts
//
// W2-S2 AC #3 — pure helpers that map a (breakpoint, clientMode) tuple to the
// per-region rendering mode + per-region default widths. Sourced from UX §3.3.

import type { Breakpoint } from './responsive-tokens.js';
import { PANEL_DEFAULT_WIDTHS } from './responsive-tokens.js';
import type { ContextPanelMode } from './ContextPanel.js';
import type { UtilityDrawerMode } from './UtilityDrawer.js';
import type { ClientMode } from '../state/navigation.js';

export interface LayoutModeResolution {
  contextPanelMode: ContextPanelMode;
  utilityDrawerMode: UtilityDrawerMode;
  contextPanelDefaultWidth: number;
  /** True when the ActivityBar must render as a horizontal top rail (RB-locked answer #1). */
  activityBarHorizontal: boolean;
}

export function resolveLayoutModes(
  breakpoint: Breakpoint,
  clientMode: ClientMode,
): LayoutModeResolution {
  // Embed mode hides the ContextPanel by default per UX §3.4 + AC #6.
  // For desktop tiers in embedded mode the panel mode collapses to `hidden`.
  // The rest of the table follows UX §3.3.
  switch (breakpoint) {
    case 'desktop-xl':
      return {
        contextPanelMode: clientMode === 'embedded' ? 'hidden' : 'inline',
        utilityDrawerMode: 'inline',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanel,
        activityBarHorizontal: false,
      };
    case 'desktop':
      return {
        contextPanelMode: clientMode === 'embedded' ? 'hidden' : 'inline',
        utilityDrawerMode: 'inline',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanelCompact,
        activityBarHorizontal: false,
      };
    case 'narrow-desktop':
      return {
        contextPanelMode: clientMode === 'embedded' ? 'hidden' : 'inline',
        utilityDrawerMode: 'popover',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanelCompact,
        activityBarHorizontal: false,
      };
    case 'tablet':
      return {
        contextPanelMode: 'overlay',
        utilityDrawerMode: 'popover',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanelCompact,
        activityBarHorizontal: false,
      };
    case 'narrow':
      return {
        contextPanelMode: 'overlay',
        utilityDrawerMode: 'popover',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanelCompact,
        activityBarHorizontal: false,
      };
    case 'embed-narrow':
      return {
        contextPanelMode: 'overlay',
        utilityDrawerMode: 'popover',
        contextPanelDefaultWidth: PANEL_DEFAULT_WIDTHS.contextPanelCompact,
        activityBarHorizontal: true,
      };
  }
}
